/**
 * The Prospects page's view, filters, page and open prospect, kept in the URL so a filtered list can
 * be bookmarked, shared with a colleague looking over the advisor's shoulder, and survives a reload.
 *
 * Changing any filter goes back to page one, since the old page number means nothing in a new list.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Prospecting } from '@client/api/types';

/** The shortest search the scoring service accepts. */
export const MIN_SEARCH_LENGTH = 2;

const FILTER_KEYS = ['view', 'tier', 'ageBand', 'assetsBand', 'lastMeeting', 'signal', 'q'] as const;
type FilterKey = (typeof FILTER_KEYS)[number];

export function useProspectFilters() {
  const [params, setParams] = useSearchParams();

  const query = useMemo<Prospecting.ProspectListQuery>(() => {
    const read = (key: string) => params.get(key) || undefined;
    return {
      view: (read('view') as Prospecting.ProspectView | undefined) ?? 'tiered',
      tier: read('tier'),
      ageBand: read('ageBand'),
      assetsBand: read('assetsBand'),
      lastMeeting: read('lastMeeting') as Prospecting.LastMeetingFilter | undefined,
      signal: read('signal') as Prospecting.ProspectSignal | undefined,
      // A one-character search can still arrive in a link. It is dropped, not sent to be refused.
      q: (read('q')?.trim().length ?? 0) >= MIN_SEARCH_LENGTH ? read('q') : undefined,
      page: Number(read('page') ?? 1),
    };
  }, [params]);

  const update = useCallback(
    (changes: Partial<Record<FilterKey | 'page' | 'prospect', string | undefined>>) => {
      setParams(
        current => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(changes)) {
            if (value) next.set(key, value);
            else next.delete(key);
          }
          if (Object.keys(changes).some(key => (FILTER_KEYS as readonly string[]).includes(key))) next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const clearFilters = useCallback(() => {
    update({ tier: undefined, ageBand: undefined, assetsBand: undefined, lastMeeting: undefined, signal: undefined, q: undefined });
  }, [update]);

  return {
    query,
    openProspectId: params.get('prospect'),
    setView: (view: Prospecting.ProspectView) => update({ view: view === 'tiered' ? undefined : view, tier: undefined }),
    setFilter: (key: Exclude<FilterKey, 'view'>, value: string | undefined) => update({ [key]: value }),
    setPage: (page: number) => update({ page: page > 1 ? String(page) : undefined }),
    openProspect: (prospectId: string | null) => update({ prospect: prospectId ?? undefined }),
    clearFilters,
    hasFilters: FILTER_KEYS.some(key => key !== 'view' && params.get(key)),
  };
}
